import React, { useEffect, useState } from "react";
import { Modal, Table, message, Tooltip, InputNumber, Button, DatePicker, Typography } from "antd";
import { ClockCircleOutlined, InfoCircleOutlined, DeleteOutlined, HistoryOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  OvertimeAccrual,
  OvertimeSummary,
  DefaultWorkHoursEntry,
  getOvertimeAccruals,
  getOvertimeSummary,
  getUserDefaultWorkHoursHistory,
  upsertUserDefaultWorkHoursMonth,
  deleteUserDefaultWorkHoursMonth,
} from "../../../../../../utils/api/overtimeAccrualAPI";
import "./OvertimeAccrualModal.scss";

const { Title, Text } = Typography;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  monthStart: string; // ISO date, start of the month being viewed
  monthEnd: string; // ISO date, end of the month being viewed
  viewedMonthKey: string; // "YYYY-MM" of the month being viewed
  onSaved?: () => void; // parent refresh — salary totals live outside this modal too
};

const monthLabel = (monthKey: string) => {
  const [y, m] = monthKey.split("-").map(Number);
  if (!y || !m) return monthKey;
  return new Date(y, m - 1, 1).toLocaleDateString("sr-RS", { month: "long", year: "numeric" });
};

// Shift durations carry minute-precision decimals (e.g. 9h07m = 9.1166...h) —
// always display hours rounded to at most 2 decimals.
const fmtH = (h: number | null | undefined) => {
  const n = Number(h) || 0;
  return Math.round(n * 100) / 100;
};

// Payout mode is SALARY-only for now (TIME_OFF/OTHER exist in the data model
// for later, but are hidden from this UI until the "slobodni dani" balance
// can actually be spent somewhere — see conversation with Boris 2026-09-21).
const OvertimeAccrualModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userId,
  monthStart,
  monthEnd,
  viewedMonthKey,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<OvertimeAccrual[]>([]);
  const [summary, setSummary] = useState<OvertimeSummary | null>(null);
  const [history, setHistory] = useState<DefaultWorkHoursEntry[]>([]);
  const [editMonth, setEditMonth] = useState<Dayjs | null>(
    viewedMonthKey ? dayjs(`${viewedMonthKey}-01`) : null,
  );
  const [hoursDraft, setHoursDraft] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEditMonth(viewedMonthKey ? dayjs(`${viewedMonthKey}-01`) : null);
  }, [viewedMonthKey, isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accruals, sum, hist] = await Promise.all([
        getOvertimeAccruals(userId, monthStart, monthEnd),
        getOvertimeSummary(userId, monthStart, monthEnd),
        getUserDefaultWorkHoursHistory(userId),
      ]);
      setRows(accruals);
      setSummary(sum);
      setHistory(hist);
    } catch (err) {
      console.error(err);
      message.error("Greška pri učitavanju prekovremenih sati.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && userId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId, monthStart, monthEnd]);

  // The entry actually in effect for the month being viewed (latest entry
  // whose month is <= the viewed month) — mirrors the backend's carry-forward
  // resolution, purely for display ("trenutno aktivno za ovaj mesec").
  const activeForViewedMonth = [...history]
    .filter((h) => h.date.slice(0, 7) <= viewedMonthKey)
    .sort((a, b) => a.date.localeCompare(b.date))
    .pop();

  const handleSave = async () => {
    if (!editMonth || hoursDraft === null) return;
    const month = editMonth.format("YYYY-MM");
    setSaving(true);
    try {
      const updated = await upsertUserDefaultWorkHoursMonth(userId, month, hoursDraft);
      setHistory(updated);
      message.success(`Sačuvano za ${monthLabel(month)} — prekovremeni sati ponovo izračunati.`);
      setHoursDraft(null);
      await loadData();
      onSaved?.();
    } catch (err) {
      console.error(err);
      message.error("Greška pri čuvanju.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (month: string) => {
    setSaving(true);
    try {
      const updated = await deleteUserDefaultWorkHoursMonth(userId, month);
      setHistory(updated);
      message.success(`Uklonjeno za ${monthLabel(month)}.`);
      await loadData();
      onSaved?.();
    } catch (err) {
      console.error(err);
      message.error("Greška pri čuvanju.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: "Datum",
      dataIndex: "date",
      render: (d: string) => new Date(d).toLocaleDateString("sr-RS"),
    },
    {
      title: "Planirano",
      dataIndex: "scheduledHours",
      render: (h: number) => `${fmtH(h)}h`,
    },
    {
      title: "Odrađeno",
      dataIndex: "actualHours",
      render: (h: number) => `${fmtH(h)}h`,
    },
    {
      title: "Prekovremeno",
      dataIndex: "overtimeHours",
      render: (h: number) => <span className="oam-ot-badge">{fmtH(h)}h</span>,
    },
  ];

  const historyColumns = [
    {
      title: "Važi od meseca",
      dataIndex: "date",
      render: (d: string) => monthLabel(d.slice(0, 7)),
    },
    {
      title: "Sati dnevno",
      dataIndex: "hours",
      render: (h: number) => `${fmtH(h)}h`,
    },
    {
      title: "",
      dataIndex: "actions",
      align: "right" as const,
      render: (_: any, row: DefaultWorkHoursEntry) => (
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          loading={saving}
          onClick={() => handleDeleteEntry(row.date.slice(0, 7))}
        />
      ),
    },
  ];

  return (
    <Modal
      title={
        <div className="oam-header">
          <ClockCircleOutlined className="oam-header-icon" />
          <div className="oam-header-text">
            <Title level={4} className="oam-header-title">
              Prekovremeni sati
            </Title>
            <Text className="oam-header-subtitle">
              Automatska detekcija na osnovu smena i radnog rasporeda
            </Text>
          </div>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      width={760}
      className="oam-modal"
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Zatvori
        </Button>,
      ]}
    >
      {/* Summary */}
      <div className="oam-summary">
        <div className="oam-summary-card">
          <Text className="oam-summary-label">Ovaj mesec — prekovremeno na platu</Text>
          <div className="oam-summary-value">{summary ? fmtH(summary.salaryHours) : "—"}h</div>
        </div>
        <div className="oam-summary-hint">
          Za <b>{monthLabel(viewedMonthKey)}</b>:{" "}
          {activeForViewedMonth ? (
            <>
              koristi se <b>{fmtH(activeForViewedMonth.hours)}h</b>/dan (postavljeno za{" "}
              {monthLabel(activeForViewedMonth.date.slice(0, 7))})
            </>
          ) : (
            "koristi se radni raspored (ugovor) — nema lične vrednosti za ovaj ili raniji mesec"
          )}
        </div>
      </div>

      {/* Personal hours editor */}
      <div className="oam-section">
        <div className="oam-section-title">
          <InfoCircleOutlined />
          Očekivani sati dnevno (lično, umesto ugovora)
          <Tooltip title="Vrednost važi od izabranog meseca pa nadalje, dok se ne unese druga vrednost za neki kasniji mesec (isto kao promena plate). Menjanje prošlog meseca ne utiče na mesece koji već imaju svoju vrednost.">
            <InfoCircleOutlined className="oam-section-title-tip" />
          </Tooltip>
        </div>
        <div className="oam-editor-row">
          <DatePicker
            picker="month"
            value={editMonth}
            onChange={(val) => setEditMonth(val)}
            className="oam-month-picker"
            format="MMMM YYYY"
          />
          <InputNumber
            min={0}
            step={0.5}
            placeholder="Sati"
            value={hoursDraft}
            onChange={(val) => setHoursDraft(val as number | null)}
            className="oam-hours-input"
          />
          <Button
            type="primary"
            loading={saving}
            onClick={handleSave}
            disabled={!editMonth || hoursDraft === null}
          >
            Sačuvaj
          </Button>
        </div>

        {history.length > 0 && (
          <>
            <div className="oam-subsection-title">
              <HistoryOutlined /> Istorija po mesecima
            </div>
            <Table
              rowKey="date"
              dataSource={history}
              columns={historyColumns}
              pagination={false}
              size="small"
              className="oam-history-table"
            />
          </>
        )}
      </div>

      {/* Detected overtime days */}
      <div className="oam-section">
        <div className="oam-section-title">
          <ClockCircleOutlined />
          Odrađeni prekovremeni dani ovog meseca
        </div>
        <Table
          rowKey="_id"
          dataSource={rows}
          columns={columns}
          loading={loading}
          pagination={false}
          size="small"
          className="oam-days-table"
          locale={{
            emptyText: activeForViewedMonth
              ? `Nema prekovremenih sati u ovom periodu (sa ${fmtH(activeForViewedMonth.hours)}h/dan, nijedan dan nije prešao taj broj).`
              : "Nema prekovremenih sati u ovom periodu.",
          }}
        />
      </div>

      <div className="oam-note">
        Ako je za mesec postavljena lična vrednost, ona se koristi za svaki dan. Ako nije,
        prekovremeno se računa samo za dane koje pokriva radni raspored (ugovor) — dani van
        rasporeda (npr. vikend van ugovora) se ne broje. Svi prekovremeni sati trenutno idu na
        platu. Za već zatvorene mesece prikaz je informativan i ne menja retroaktivno obračunatu
        platu.
      </div>
    </Modal>
  );
};

export default OvertimeAccrualModal;
